/**
 * Schema.org utilities for Alora Rejser
 * Generates valid JSON-LD structured data for Google Rich Results.
 */

const SITE_URL = 'https://alorarejser.dk';
const LOGO_URL = 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-fav.png';

/**
 * Builds the canonical Organization & LocalBusiness (TravelAgency) Schema for Alora Rejser.
 * Uses a single unified entity with @id: https://alorarejser.dk/#organization to prevent
 * duplicate/conflicting entities and resolve Google Rich Results non-critical issues.
 */
export const buildOrganizationSchema = ({ language = 'da', asTravelAgency = false } = {}) => {
  const isEn = language === 'en';
  const org = {
    '@type': asTravelAgency ? ['TravelAgency', 'Organization'] : 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Alora Rejser',
    legalName: 'Alora Rejser',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/#logo`,
      url: LOGO_URL,
      contentUrl: LOGO_URL,
      caption: 'Alora Rejser'
    },
    image: LOGO_URL,
    description: isEn
      ? 'Specialist in accessible, wheelchair-friendly holidays and hotels for travelers with reduced mobility in Europe.'
      : 'Specialist i handicapvenlige rejser og tilgængelige hoteller for kørestolsbrugere og gangbesværede i Europa.',
    telephone: '+4540500780',
    email: 'info@alorarejser.dk',
    vatID: 'DK46391772',
    taxID: '46391772',
    ...(asTravelAgency ? { priceRange: '$$' } : {}),
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Gjessøvej 56',
      postalCode: '8600',
      addressLocality: 'Silkeborg',
      addressCountry: 'DK'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+4540500780',
      contactType: 'customer service',
      email: 'info@alorarejser.dk',
      availableLanguage: ['Danish', 'English']
    },
    sameAs: [
      'https://www.facebook.com/profile.php?id=61572030426651'
    ]
  };

  return org;
};

export const toAbsoluteUrl = (url = '') => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Transforms an Auxo CDN asset URL to an on-the-fly Cloudflare resized variant.
 * Avoids loading multi-megabyte raw originals and prevents crawl timeouts.
 */
export const getOptimizedImageUrl = (url, width = 800, quality = 80) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.startsWith('https://assets.auxodev.dk/') || url.includes('/cdn-cgi/image/')) {
    return url;
  }
  return url.replace(
    'https://assets.auxodev.dk/',
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`
  );
};

/**
 * Parses Danish and English date strings (e.g. "maj 2025", "May 2025", "15. maj 2025") into ISO YYYY-MM-DD
 */
export const parsePublishDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return '2025-05-01';

  // Already ISO YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
    return dateStr.trim();
  }

  const monthMap = {
    januar: '01', jan: '01', january: '01',
    februar: '02', feb: '02', february: '02',
    marts: '03', mar: '03', march: '03',
    april: '04', apr: '04',
    maj: '05', may: '05',
    juni: '06', jun: '06', june: '06',
    juli: '07', jul: '07', july: '07',
    august: '08', aug: '08',
    september: '09', sep: '09', sept: '09',
    oktober: '10', okt: '10', oct: '10', october: '10',
    november: '11', nov: '11',
    december: '12', dec: '12'
  };

  const clean = dateStr.toLowerCase().trim();
  const yearMatch = clean.match(/\b(20\d{2})\b/);
  const year = yearMatch ? yearMatch[1] : '2025';

  let month = '05';
  for (const [name, num] of Object.entries(monthMap)) {
    if (new RegExp(`\\b${name}\\b`, 'i').test(clean)) {
      month = num;
      break;
    }
  }

  const dayMatch = clean.match(/\b(\d{1,2})\./) || clean.match(/\b(\d{1,2})\b/);
  let day = '01';
  if (dayMatch && dayMatch[1] && dayMatch[1] !== year) {
    const d = parseInt(dayMatch[1], 10);
    if (d >= 1 && d <= 31) {
      day = d.toString().padStart(2, '0');
    }
  }

  return `${year}-${month}-${day}`;
};

/**
 * Builds a BreadcrumbList Schema
 * @param {Array<{ name: string, url: string }>} items
 * @param {string} [currentUrl]
 */
export const buildBreadcrumbSchema = (items = [], currentUrl = '') => {
  if (!items || items.length === 0) return null;

  const breadcrumbId = currentUrl ? `${toAbsoluteUrl(currentUrl)}#breadcrumb` : undefined;

  return {
    '@type': 'BreadcrumbList',
    ...(breadcrumbId ? { '@id': breadcrumbId } : {}),
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.url)
    }))
  };
};

/**
 * Builds an Article / BlogPosting Schema conforming to Google Rich Results guidelines
 */
export const buildArticleSchema = ({
  title,
  description,
  image,
  imageLarge,
  datePublished,
  dateModified,
  url,
  language = 'da',
  authorName = 'Alora Rejser',
  category,
  categories = []
}) => {
  if (!title) return null;

  const isEn = language === 'en';
  const isoDate = parsePublishDate(datePublished);
  const modifiedIsoDate = dateModified ? parsePublishDate(dateModified) : isoDate;
  const fullUrl = toAbsoluteUrl(url);

  // Collect and optimize images (ensuring optimal 1200px width for Google Discover & Rich Results)
  const rawImages = [imageLarge, image].filter(Boolean);
  const imageCandidates = rawImages.map(img => getOptimizedImageUrl(toAbsoluteUrl(img), 1200, 85));
  const finalImages = imageCandidates.length > 0 ? imageCandidates : [LOGO_URL];

  const blogPath = isEn ? '/en/travel-tips' : '/da/rejsetips';
  const section = category || (categories && categories.length > 0 ? categories[0] : (isEn ? 'Travel Tips' : 'Rejsetips'));
  const keywordsList = categories && categories.length > 0 ? categories : (category ? [category] : []);

  return {
    '@type': 'BlogPosting',
    '@id': `${fullUrl}#article`,
    headline: title,
    name: title,
    description: description || title,
    image: finalImages.length === 1 ? finalImages[0] : finalImages,
    datePublished: isoDate,
    dateModified: modifiedIsoDate,
    inLanguage: isEn ? 'en-US' : 'da-DK',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl
    },
    url: fullUrl,
    ...(section ? { articleSection: section } : {}),
    ...(keywordsList.length > 0 ? { keywords: keywordsList.join(', ') } : {}),
    isPartOf: {
      '@type': 'Blog',
      '@id': `${toAbsoluteUrl(blogPath)}#blog`,
      name: isEn ? 'Alora Rejser Travel Tips & Guides' : 'Alora Rejser Rejsetips & Guides'
    },
    author: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: authorName || 'Alora Rejser',
      url: SITE_URL
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Alora Rejser',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        '@id': `${SITE_URL}/#logo`,
        url: LOGO_URL
      }
    }
  };
};

/**
 * Builds a comprehensive Blog and ItemList schema containing all articles
 * Ensures every single article is represented in Schema.org structured data.
 */
export const buildBlogWithArticlesSchema = ({
  articles = [],
  language = 'da',
  currentUrl = ''
}) => {
  const isEn = language === 'en';
  const traveltipsPath = currentUrl || (isEn ? '/en/travel-tips' : '/da/rejsetips');
  const fullUrl = toAbsoluteUrl(traveltipsPath);

  const blogPostItems = articles.map((art) => {
    const artPath = art.routePath || `${traveltipsPath}?article=${art.id}`;
    return buildArticleSchema({
      title: art.title,
      description: art.intro,
      image: art.image,
      imageLarge: art.imageLarge,
      datePublished: art.date,
      url: artPath,
      language,
      category: art.category,
      categories: art.categories
    });
  }).filter(Boolean);

  const blog = {
    '@type': 'Blog',
    '@id': `${fullUrl}#blog`,
    name: isEn ? 'Alora Rejser Travel Tips & Guides' : 'Alora Rejser Rejsetips & Guides',
    description: isEn
      ? 'Get advice, guides, and practical travel tips for accessible travel, wheelchair-friendly holidays, and accessible hotels.'
      : 'Få gode råd, guides og praktiske rejsetips til handicaprejser, kørestolsvenlige ferier og tilgængelige hoteller.',
    url: fullUrl,
    inLanguage: isEn ? 'en-US' : 'da-DK',
    publisher: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Alora Rejser',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        '@id': `${SITE_URL}/#logo`,
        url: LOGO_URL
      }
    },
    blogPost: blogPostItems
  };

  const itemList = {
    '@type': 'ItemList',
    '@id': `${fullUrl}#article-list`,
    name: isEn ? 'Articles and Travel Tips' : 'Artikler og rejsetips',
    description: isEn
      ? 'Complete list of guides and travel tips for accessible travel'
      : 'Komplet oversigt over guides og rejsetips til handicapvenlige rejser',
    numberOfItems: blogPostItems.length,
    itemListElement: blogPostItems.map((post, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: post.headline,
      url: post.url,
      item: post
    }))
  };

  return { blog, itemList, blogPostItems };
};

/**
 * Builds a Hotel Schema with accessibility amenity features
 */
export const buildHotelSchema = ({
  hotel,
  destinationTitle = '',
  description = '',
  url = '',
  language = 'da'
}) => {
  if (!hotel) return null;

  const rawImages = (hotel.gallery && hotel.gallery.length > 0 ? hotel.gallery : [hotel.image || ''])
    .filter(Boolean);

  // Provide up to 6 high-resolution optimized images for Googlebot.
  // Using Cloudflare resizing avoids multi-megabyte crawler timeouts while providing crisp 1200px images.
  const images = rawImages
    .slice(0, 6)
    .map(img => getOptimizedImageUrl(toAbsoluteUrl(img), 1200, 85));

  const amenityMap_da = {
    pool_lift: 'Pool lift (kørestolslift til pool)',
    elevator: 'Elevator',
    vaerelse: 'Handicapvenligt værelse med brede døre',
    badestol: 'Badekørestol / brusebænk',
    greb: 'Støttegreb ved toilet og bruser',
    romertrappe: 'Romertrappe med gelænder til pool',
    strand: 'Handicapvenlig strand med træramper / badebro',
    parkering: 'Handicapvenlig parkering',
    terraen: 'Fladt og barrierefrit terræn',
    liftbus: 'Lufthavnstransport med liftbus',
    lufthavnsassistance: 'Lufthavnsassistance'
  };

  const amenityMap_en = {
    pool_lift: 'Pool hoist / pool lift for wheelchair users',
    elevator: 'Elevator',
    vaerelse: 'Wheelchair accessible room with wide doors',
    badestol: 'Shower wheelchair / shower chair',
    greb: 'Grab bars in bathroom and shower',
    romertrappe: 'Roman stairs with handrail to pool',
    strand: 'Accessible beach with wooden ramp',
    parkering: 'Accessible parking',
    terraen: 'Flat and barrier-free terrain',
    liftbus: 'Wheelchair accessible airport transfer',
    lufthavnsassistance: 'Airport assistance'
  };

  const amenityLabels = language === 'en' ? amenityMap_en : amenityMap_da;

  const amenityFeatures = [];
  
  // General wheelchair accessible feature
  amenityFeatures.push({
    '@type': 'LocationFeatureSpecification',
    name: language === 'en' ? 'Wheelchair accessible' : 'Kørestolsvenlig adgang',
    value: true
  });

  if (Array.isArray(hotel.accessibilityIcons)) {
    hotel.accessibilityIcons.forEach((key) => {
      if (amenityLabels[key]) {
        amenityFeatures.push({
          '@type': 'LocationFeatureSpecification',
          name: amenityLabels[key],
          value: true
        });
      }
    });
  }

  // Also include notable features
  if (Array.isArray(hotel.features)) {
    hotel.features.forEach((feature) => {
      if (feature && !amenityFeatures.some(f => f.name.toLowerCase() === feature.toLowerCase())) {
        amenityFeatures.push({
          '@type': 'LocationFeatureSpecification',
          name: feature,
          value: true
        });
      }
    });
  }

  // Determine country code from destinationId or title
  const destinationId = (hotel.destinationId || '').toLowerCase();
  const countryMap = {
    mallorca: 'ES',
    kreta: 'GR',
    rhodos: 'GR',
    tenerife: 'ES',
    'gran-canaria': 'ES',
    paris: 'FR',
    venedig: 'IT',
    oestrig: 'AT',
    'zell-am-see': 'AT'
  };

  let countryCode = countryMap[destinationId];
  if (!countryCode && destinationTitle) {
    const titleLower = destinationTitle.toLowerCase();
    if (titleLower.includes('mallorca') || titleLower.includes('spanien') || titleLower.includes('spain')) countryCode = 'ES';
    else if (titleLower.includes('kret') || titleLower.includes('rhod') || titleLower.includes('græk') || titleLower.includes('gree')) countryCode = 'GR';
    else if (titleLower.includes('paris') || titleLower.includes('frankrig') || titleLower.includes('france')) countryCode = 'FR';
    else if (titleLower.includes('venedig') || titleLower.includes('venice') || titleLower.includes('ital')) countryCode = 'IT';
    else if (titleLower.includes('østrig') || titleLower.includes('austria') || titleLower.includes('zell')) countryCode = 'AT';
  }

  const fullUrl = toAbsoluteUrl(url);
  const priceLevel = hotel.stars >= 5 ? '$$$$' : (hotel.stars >= 4 ? '$$$' : '$$');

  const address = {
    '@type': 'PostalAddress',
    addressLocality: hotel.area || '',
    addressRegion: destinationTitle || '',
    addressCountry: countryCode || 'ES'
  };
  if (hotel.streetAddress) {
    address.streetAddress = hotel.streetAddress;
  }
  if (hotel.postalCode) {
    address.postalCode = hotel.postalCode;
  }

  const schema = {
    '@type': 'Hotel',
    '@id': `${fullUrl}#hotel`,
    name: hotel.name,
    description: description || hotel.description || '',
    image: images.length > 0 ? images : undefined,
    url: fullUrl,
    starRating: hotel.stars ? {
      '@type': 'Rating',
      ratingValue: hotel.stars,
      bestRating: 5,
      worstRating: 1
    } : undefined,
    priceRange: priceLevel,
    address,
    breadcrumb: {
      '@id': `${fullUrl}#breadcrumb`
    },
    amenityFeature: amenityFeatures.length > 0 ? amenityFeatures : undefined
  };

  // Check optional real properties if present
  if (hotel.telephone) {
    schema.telephone = hotel.telephone;
  }
  if (hotel.email) {
    schema.email = hotel.email;
  }
  if (hotel.checkinTime) {
    schema.checkinTime = hotel.checkinTime;
  }
  if (hotel.checkoutTime) {
    schema.checkoutTime = hotel.checkoutTime;
  }
  if (hotel.sameAs && (Array.isArray(hotel.sameAs) ? hotel.sameAs.length > 0 : Boolean(hotel.sameAs))) {
    schema.sameAs = hotel.sameAs;
  }
  if (hotel.geo && typeof hotel.geo === 'object' && hotel.geo.latitude && hotel.geo.longitude) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: hotel.geo.latitude,
      longitude: hotel.geo.longitude
    };
  } else if (hotel.latitude && hotel.longitude) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: hotel.latitude,
      longitude: hotel.longitude
    };
  }

  if (destinationTitle) {
    schema.containedInPlace = {
      '@type': 'TouristDestination',
      name: destinationTitle
    };
  }

  return schema;
};

/**
 * Builds a TouristDestination Schema
 */
export const buildDestinationSchema = ({
  destination,
  url = '',
  language = 'da'
}) => {
  if (!destination) return null;

  const title = language === 'en' ? (destination.title_en || destination.title) : destination.title;
  const description = language === 'en' ? (destination.description_en || destination.description) : destination.description;

  return {
    '@type': 'TouristDestination',
    name: title,
    description: description || '',
    image: toAbsoluteUrl(destination.image),
    url: toAbsoluteUrl(url),
    touristType: [
      language === 'en' ? 'Accessible travel' : 'Handicapvenlige rejser',
      language === 'en' ? 'Wheelchair users' : 'Kørestolsbrugere'
    ]
  };
};

/**
 * Normalizes one or multiple schema objects into a valid Schema.org structure with @context and @graph
 */
export const combineSchemas = (...schemas) => {
  const flattened = schemas
    .flat(Infinity)
    .filter(Boolean);

  if (flattened.length === 0) return null;

  const items = [];
  flattened.forEach((item) => {
    if (typeof item !== 'object' || item === null) return;
    if (Array.isArray(item['@graph'])) {
      item['@graph'].forEach((subItem) => {
        if (subItem && typeof subItem === 'object') {
          const copy = { ...subItem };
          delete copy['@context'];
          items.push(copy);
        }
      });
    } else {
      const copy = { ...item };
      delete copy['@context'];
      items.push(copy);
    }
  });

  // Deduplicate by @id if present
  const seenIds = new Set();
  const dedupedItems = items.filter((item) => {
    if (item['@id']) {
      if (seenIds.has(item['@id'])) return false;
      seenIds.add(item['@id']);
    }
    return true;
  });

  if (dedupedItems.length === 0) return null;

  if (dedupedItems.length === 1) {
    return {
      '@context': 'https://schema.org',
      ...dedupedItems[0]
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': dedupedItems
  };
};
